import type { ChecklistItem } from 'services/api/checklistClient';
import type { MeetingResponse } from 'services/api/meetingsClient';
import { meetingDateKey, resolveMeetingTimeZone } from 'utils/meetingTime';

export type ComingUpItem =
  | { kind: 'todo'; key: string; date: string; overdue: boolean; item: ChecklistItem }
  | { kind: 'meeting'; key: string; date: string; overdue: false; item: MeetingResponse };

export const localCalendarDate = (instant: Date, timezone: string): string => {
  return meetingDateKey(instant, resolveMeetingTimeZone(undefined, timezone));
};

export const validTaskDate = (value?: string | null): value is string => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const buildComingUpItems = (
  meetings: MeetingResponse[], tasks: ChecklistItem[], timezone: string, now = new Date(),
): ComingUpItem[] => {
  const today = localCalendarDate(now, timezone);
  const items: ComingUpItem[] = [];
  tasks.forEach((item) => {
    if (!item.completedAt && validTaskDate(item.dueDate)) {
      items.push({ kind: 'todo', key: `todo-${item.id}`, date: item.dueDate, overdue: item.dueDate < today, item });
    }
  });
  meetings.forEach((item) => {
    const start = new Date(item.start_time);
    // The upcoming endpoint owns eligibility, including ongoing/late-join meetings.
    if (Number.isFinite(start.getTime())) {
      items.push({ kind: 'meeting', key: `meeting-${item.id}`, date: localCalendarDate(start, timezone), overdue: false, item });
    }
  });
  return items.sort((left, right) => {
    if (left.overdue !== right.overdue) return left.overdue ? -1 : 1;
    const dateOrder = left.date.localeCompare(right.date);
    if (dateOrder) return dateOrder;
    if (left.kind !== right.kind) return left.kind === 'todo' ? -1 : 1;
    if (left.kind === 'meeting' && right.kind === 'meeting') {
      return new Date(left.item.start_time).getTime() - new Date(right.item.start_time).getTime();
    }
    return 0;
  });
};

export const calendarDateLabel = (date: string, today: string): string =>
  date === today ? 'Today' : new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
